import { AppBackground } from "@/components/backgrounds/app-background.tsx";
import { GlobalUploadProvider } from "@/components/global-uploads/index.ts";
import {
	connectActivityStream,
	disconnectActivityStream,
	subscribeChatTurn,
	subscribeNotifications,
	subscribeSpaceAccessChange,
} from "@/hooks/activity-stream.ts";
import { notificationsQueryKey } from "@/services/notifications-service.ts";
import { invalidateChatAfterExternalTurn } from "@/services/chats-service.ts";
import { AppSidebar } from "@/components/navigation/app-sidebar.tsx";
import {
	persistSidebarWidth,
	readSidebarWidth,
} from "@/components/navigation/sidebar-nav.tsx";
import { PaymentFailedBanner } from "@/components/navigation/payment-failed-banner.tsx";
import { SystemStatusBanner } from "@/components/navigation/system-status-banner.tsx";
import { FloatingSpacesProvider } from "@/components/spaces/context/floating-spaces-context.tsx";
import { FloatingSpaceDialogs } from "@/components/spaces/floating/floating-space-dialogs.tsx";
import {
	SidebarInset,
	SidebarProvider,
	SidebarTrigger,
} from "@/components/ui/sidebar.tsx";

import { invalidateProjectLinkQueries } from "@/services/projects-service.ts";
import { ChatModelProvider } from "@/providers/chat-model-provider.tsx";
import { ImageModelProvider } from "@/providers/image-model-provider.tsx";
import { ReleaseProvider } from "@/providers/release-provider.tsx";
import { modelCatalogQueryOptions } from "@/services/model-catalog-service.ts";
import { organizationQueryOptions } from "@/services/organization-service.ts";
import { PAGE_CONTAINER_CLASS } from "@/styles/layout.ts";
import { Z_LAYERS } from "@/styles/z-layers.ts";
import {
	createFileRoute,
	Outlet,
	useLocation,
	useRouterState,
} from "@tanstack/react-router";
import { PageEnter } from "@/components/custom/page-enter.tsx";
import { pruneStaleDrafts } from "@/utils/draft-storage.ts";
import { requireActiveOnboardedUser } from "@/utils/route-auth-guard.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { cn } from "../../utils/cn.ts";
import { MotionLoaderFullscreen } from "@/components/custom/motion-loader.tsx";
import { PasskeySetupPrompt } from "@/components/auth/passkey-setup-prompt.tsx";
import { UnoPanel } from "@/components/uno/uno-panel.tsx";
import { UnoPanelProvider } from "@/components/uno/uno-panel-context.tsx";
import { UnoWelcomeNudge } from "@/components/uno/uno-welcome-nudge.tsx";
import { m } from "@/paraglide/messages.js";

export const Route = createFileRoute("/_authenticated")({
	beforeLoad: ({ context, location }) =>
		requireActiveOnboardedUser({ context, location }),
	loader: ({ context: { queryClient } }) => {
		queryClient.prefetchQuery(organizationQueryOptions());
		queryClient.prefetchQuery(modelCatalogQueryOptions());
	},
	component: () => <AuthenticatedLayoutComponent />,
	pendingComponent: () => <MotionLoaderFullscreen text={m.common_loading()} />,
});

function AuthenticatedLayoutComponent() {
	const location = useLocation();
	const isChatRoute = location.pathname.includes("/chats/");
	// Settings shares the one app sidebar (AppSidebar swaps its body by route);
	// only the content container differs — full-height instead of the centered
	// max-width dashboard column.
	const isSettingsRoute = location.pathname.startsWith("/settings");
	const isFullBleed = isSettingsRoute;
	// Which page the content pane shows, for its entrance. Home and a chat are
	// one page as far as motion goes: sending the first message moves from one to
	// the other, and the conversation must not jump while it starts. Settings
	// animates its own sections inside its shell.
	const pageKey = useRouterState({
		select: (state) => {
			const leaf = state.matches.at(-1)?.routeId ?? "";
			if (leaf.startsWith("/_authenticated/settings")) {
				return "settings";
			}
			if (
				leaf === "/_authenticated/home" ||
				leaf === "/_authenticated/dashboard/chats/$chatId"
			) {
				return "chat";
			}
			return leaf;
		},
	});
	const isMobile = window.innerWidth < 768;

	// The live width follows the drag; persistence is deliberately separate. A
	// drag fires `onWidthChange` on every pointermove, and `localStorage` writes
	// are synchronous — so the width is only committed once the gesture ends.
	const [sidebarWidth, setSidebarWidth] = useState(() => readSidebarWidth());

	const queryClient = useQueryClient();

	// Unsent drafts nobody came back to (a chat deleted elsewhere, a project
	// deleted with its chats) would otherwise sit in localStorage forever.
	useEffect(() => {
		pruneStaleDrafts();
	}, []);

	// Connect the unified activity stream (syncs, embeddings, distillations)
	useEffect(() => {
		connectActivityStream();
		return () => disconnectActivityStream();
	}, []);

	// Invalidate spaces/projects queries when access changes (e.g. project links
	// updated). The link-derived half goes through the service's own invalidation
	// set rather than a second list here: a link can now be written by the server
	// mid-chat (`create_dataset_space`), and the two paths drifting is what leaves
	// a freshly linked space still badged "Not linked" in the mention palette.
	useEffect(() => {
		return subscribeSpaceAccessChange(() => {
			queryClient.invalidateQueries({ queryKey: ["get", "/api/spaces"] });
			queryClient.invalidateQueries({
				queryKey: ["get", "/api/stats/dashboard"],
			});
			invalidateProjectLinkQueries(queryClient);
		});
	}, [queryClient]);

	// A notification poke carries no payload on purpose: re-read the inbox and let
	// the server stay the single source of truth. That is what makes a duplicate or
	// dropped poke harmless — the row is already durable, so the worst case is a
	// stale badge until the next navigation.
	useEffect(() => {
		return subscribeNotifications(() => {
			queryClient.invalidateQueries({
				queryKey: notificationsQueryKey,
			});
		});
	}, [queryClient]);

	// A turn sent from outside the browser (n8n, a bot, MCP), announced when it
	// starts and when it is stored. The first re-read reports the chat as
	// streaming, which makes an open chat attach to the stream
	// (chat-interface-websocket.tsx); the second shows the stored turn, and marks
	// a chat that is not open stale for its next visit.
	useEffect(() => {
		return subscribeChatTurn((chatId) => {
			invalidateChatAfterExternalTurn(queryClient, chatId);
		});
	}, [queryClient]);

	// The main sidebar is not collapsible: pin it open so the toggle and
	// keyboard shortcut can never hide it. Mobile still uses the sheet.
	// The width is resizable (see AppSidebar) and shared across routes; the
	// width transition is disabled so the drag tracks the pointer exactly.
	return (
		<SidebarProvider
			open
			onOpenChange={() => {}}
			style={{ "--sidebar-width": `${sidebarWidth}px` } as React.CSSProperties}
			className="[&_[data-slot=sidebar-gap]]:transition-none [&_[data-slot=sidebar-container]]:transition-none"
		>
			<UnoPanelProvider>
				{/* Inside UnoPanelProvider: the release dialog's "Hey Uno" action
				    opens the panel with a changelog seed. */}
				<ReleaseProvider>
					<FloatingSpacesProvider>
						<GlobalUploadProvider>
							<AppSidebar
								width={sidebarWidth}
								onWidthChange={setSidebarWidth}
								onWidthCommit={persistSidebarWidth}
							/>
							<SidebarInset className="flex h-svh flex-col overflow-hidden">
								<AppBackground />
								<SystemStatusBanner />
								<PaymentFailedBanner />
								{isMobile && (
									<header className="flex h-16 shrink-0 items-center gap-2 px-4 md:hidden border-b">
										<SidebarTrigger className="-ml-1" />
									</header>
								)}
								<ChatModelProvider>
									<ImageModelProvider>
										<div
											className={cn(
												!isChatRoute &&
													!isFullBleed &&
													"flex-1 min-h-0 overflow-y-auto",
												isFullBleed && "flex-1 min-h-0",
											)}
											style={{ zIndex: Z_LAYERS.content }}
										>
											<PageEnter
												pageKey={pageKey}
												className={cn(
													!isChatRoute &&
														!isFullBleed && [
															PAGE_CONTAINER_CLASS,
															"flex min-h-full flex-col gap-2 lg:gap-4",
														],
													isFullBleed && "h-full",
												)}
											>
												<Outlet />
											</PageEnter>
										</div>
									</ImageModelProvider>
								</ChatModelProvider>
								<FloatingSpaceDialogs />
								<PasskeySetupPrompt />
							</SidebarInset>
							<UnoPanel />
							<UnoWelcomeNudge />
						</GlobalUploadProvider>
					</FloatingSpacesProvider>
				</ReleaseProvider>
			</UnoPanelProvider>
		</SidebarProvider>
	);
}
